/**
 * ScenarioFamilyModal — shows all variants of a scenario family grouped by severity level.
 * Opened from the search page when clicking a scenario card's severity footer.
 */
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Heart, Baby, Ambulance, Activity, ArrowRight } from "lucide-react";
import type { CardTheme } from "@/components/ScenarioCard";

// Severity gradient (no-complications → critical). Universal green→red ramp;
// kept brand-agnostic so the semantic reads correctly on every brand without
// the middle slot pulling in patriotic gold on a Luigi's-skinned modal.
const COMP_COLORS = ['#2b5e49', '#5a9a3c', '#b8941f', '#e67e22', '#c0392b'];
// Cost-quartile labels — the family modal sorts variants by chargemaster
// price, not clinical urgency, so calling level 4 "Critical" overstates what
// the pipeline actually classifies. Honest labels match the dollar ranges
// already rendered next to each pill.
const COMP_LABELS = ['Base', 'Low-cost', 'Mid-cost', 'High-cost', 'Top-cost'];
// PHASE_COLORS for the per-phase cost-breakdown bars. Patriotic slots derived
// from the theme prop inside the component body (see usage site below) so the
// bars adopt the active brand palette. Tail slots stay neutral for deep
// multi-phase scenarios.
const PHASE_NEUTRAL_TAIL = ["#2b5e49", "#6B4C8A", "#D4763B"];
const ICON_MAP: Record<string, any> = { Heart, Baby, Ambulance, Activity };
const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";

/** Raw scenario from static JSON (camelCase fields) */
export interface RawScenario {
  id?: string;
  uuid?: string;
  name: string;
  description?: string;
  iconName?: string;
  icon_name?: string;
  color?: string;
  duration?: string;
  totalProcedures?: number;
  total_procedures?: number;
  phases?: any[];
  estimatedCost?: number;
  estimated_cost?: number;
  realTotalCost?: number | null;
  real_total_cost?: number | null;
  severityLevel?: number | null;
  severity_level?: number | null;
  phaseBreakdown?: Record<string, number>;
  scenarioFamilyId?: string | null;
  scenario_family_id?: string | null;
  familyVariantLabel?: string | null;
  family_variant_label?: string | null;
}

interface ScenarioFamilyModalProps {
  /** The base scenario that was clicked */
  scenario: {
    id: string;
    name: string;
    description: string;
    icon_name: string;
    color: string;
    scenario_family_id: string | null;
  };
  /** All raw scenarios (unfiltered, includes variants) */
  allRawScenarios: RawScenario[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  theme: CardTheme;
  /** Optional callback for variant selection. When provided, overrides default navigation to /scenarios/. */
  onVariantSelect?: (scenario: RawScenario) => void;
  /** When set, scrolls the matching severity section into view as the modal opens.
   *  Lets the ScenarioCard's per-pill click drop straight into the right bucket. */
  defaultLevel?: number | null;
}

/** Compute severity levels: base=0, others by cost percentile */
function computeFamilySeverity(familyMembers: RawScenario[]): Map<string, number> {
  const map = new Map<string, number>();
  if (familyMembers.length <= 1) {
    familyMembers.forEach(s => map.set(s.id || s.uuid || '', 0));
    return map;
  }

  // Base = scenarios with severityLevel 0
  const baseIds = new Set<string>();
  familyMembers.forEach(s => {
    const id = s.id || s.uuid || '';
    if ((s.severityLevel ?? s.severity_level ?? 0) === 0) baseIds.add(id);
  });

  // Fallback: if ALL are 0, use cheapest as base
  if (baseIds.size === 0 || baseIds.size === familyMembers.length) {
    baseIds.clear();
    const cheapest = [...familyMembers].sort((a, b) =>
      (a.estimatedCost ?? a.estimated_cost ?? 0) - (b.estimatedCost ?? b.estimated_cost ?? 0)
    )[0];
    baseIds.add(cheapest.id || cheapest.uuid || '');
  }

  baseIds.forEach(id => map.set(id, 0));

  // Cost percentile for non-base
  const nonBase = familyMembers
    .filter(s => !baseIds.has(s.id || s.uuid || ''))
    .sort((a, b) => (a.estimatedCost ?? a.estimated_cost ?? 0) - (b.estimatedCost ?? b.estimated_cost ?? 0));

  const n = nonBase.length;
  nonBase.forEach((s, idx) => {
    const id = s.id || s.uuid || '';
    if (n <= 1) { map.set(id, 4); return; }
    const pctRank = idx / (n - 1);
    map.set(id, pctRank <= 0.25 ? 1 : pctRank <= 0.50 ? 2 : pctRank <= 0.75 ? 3 : 4);
  });
  return map;
}

export function ScenarioFamilyModal({
  scenario,
  allRawScenarios,
  open,
  onOpenChange,
  theme: T,
  onVariantSelect,
  defaultLevel,
}: ScenarioFamilyModalProps) {
  const { t } = useTranslation(['homepage']);
  const navigate = useNavigate();
  // Per-level section refs so a deep-link from ScenarioCard can scroll the
  // requested severity bucket into view right as the modal opens.
  const levelRefs = React.useRef<Record<number, HTMLDivElement | null>>({});
  React.useEffect(() => {
    if (!open || defaultLevel == null) return;
    const lv = Math.min(Math.max(Math.round(defaultLevel), 0), 4);
    const t = setTimeout(() => {
      levelRefs.current[lv]?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => clearTimeout(t);
  }, [open, defaultLevel]);
  // Phase bar colors riding the active brand palette via the theme prop —
  // matches ScenarioCard's brand-aware approach so the family modal and the
  // card it expands from stay color-consistent.
  const phaseColors = useMemo(
    () => [T.red, T.blue, T.gold, ...PHASE_NEUTRAL_TAIL],
    [T.red, T.blue, T.gold]
  );

  // Find all family members
  const familyMembers = useMemo(() => {
    if (!scenario.scenario_family_id) return [];
    return allRawScenarios.filter(s => {
      const fid = s.scenarioFamilyId || s.scenario_family_id;
      return fid === scenario.scenario_family_id;
    });
  }, [scenario.scenario_family_id, allRawScenarios]);

  // Compute severity map
  const severityMap = useMemo(() => computeFamilySeverity(familyMembers), [familyMembers]);

  // Group by severity level
  const byLevel = useMemo(() => {
    const groups: Record<number, RawScenario[]> = {};
    familyMembers.forEach(s => {
      const id = s.id || s.uuid || '';
      const lvl = Math.min(Math.max(severityMap.get(id) ?? 0, 0), 4);
      if (!groups[lvl]) groups[lvl] = [];
      groups[lvl].push(s);
    });
    // Sort each group by cost
    Object.values(groups).forEach(arr =>
      arr.sort((a, b) => (a.estimatedCost ?? a.estimated_cost ?? 0) - (b.estimatedCost ?? b.estimated_cost ?? 0))
    );
    return groups;
  }, [familyMembers, severityMap]);

  const baseCost = useMemo(() => {
    const baseScenarios = byLevel[0] || [];
    if (baseScenarios.length === 0) return 0;
    return baseScenarios[0].estimatedCost ?? baseScenarios[0].estimated_cost ?? 0;
  }, [byLevel]);

  // Cost range info per level
  const levelRanges = useMemo(() => {
    const compCosts = familyMembers
      .filter(s => (severityMap.get(s.id || s.uuid || '') ?? 0) > 0)
      .map(s => s.estimatedCost ?? s.estimated_cost ?? 0)
      .sort((a, b) => a - b);

    const q = (arr: number[], p: number) => {
      if (arr.length === 0) return 0;
      const idx = Math.max(0, Math.ceil(arr.length * p) - 1);
      return arr[idx];
    };

    return [
      { min: baseCost, max: baseCost },
      { min: compCosts[0] || 0, max: q(compCosts, 0.25) },
      { min: q(compCosts, 0.25), max: q(compCosts, 0.50) },
      { min: q(compCosts, 0.50), max: q(compCosts, 0.75) },
      { min: q(compCosts, 0.75), max: q(compCosts, 1.0) },
    ];
  }, [familyMembers, severityMap, baseCost]);

  const fmt = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `$${Math.round(n).toLocaleString()}`;
  const fmtFull = (n: number) => `$${Math.round(n).toLocaleString()}`;
  const IconComponent = ICON_MAP[scenario.icon_name] || Activity;
  const MAX_CARDS_PER_LEVEL = 3;

  const handleVariantClick = (s: RawScenario) => {
    if (onVariantSelect) {
      onVariantSelect(s);
      onOpenChange(false);
      return;
    }
    const id = s.id || s.uuid || '';
    const slug = s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    onOpenChange(false);
    navigate(`/scenarios/${id}/${slug}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" style={{
        backgroundColor: T.cardBg,
        border: `1px solid ${T.border}`,
      }}>
        {/* Header */}
        <div className="p-6 pb-3">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-lg flex-shrink-0" style={{ backgroundColor: `${scenario.color}15` }}>
              <IconComponent className="w-6 h-6" style={{ color: scenario.color }} />
            </div>
            <div className="flex-1 min-w-0 pr-8">
              <h2 style={{ fontFamily: serif, fontSize: 24, fontWeight: 400, color: T.text }}>
                {scenario.name}
              </h2>
              <p className="mt-1 line-clamp-2" style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.45 }}>
                {scenario.description}
              </p>
              <p className="mt-2" style={{ fontSize: 12, color: T.textMuted }}>
                {t('homepage:homeBeta.familyModal.scenariosAcross', { members: familyMembers.length, levels: Object.keys(byLevel).length })}
              </p>
            </div>
          </div>

          {/* Summary cost range chips */}
          <div className="flex gap-1.5 overflow-x-auto mt-4" style={{ scrollbarWidth: 'none' }}>
            {[0, 1, 2, 3, 4].map(level => {
              const count = (byLevel[level] || []).length;
              const r = levelRanges[level];
              return (
                <div key={level} className="flex-1 min-w-[90px] rounded-lg p-2" style={{
                  backgroundColor: count > 0 ? `${COMP_COLORS[level]}08` : 'rgba(128,128,128,0.04)',
                  border: `1px solid ${count > 0 ? `${COMP_COLORS[level]}22` : 'rgba(128,128,128,0.1)'}`,
                }}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0" style={{
                      backgroundColor: count > 0 ? COMP_COLORS[level] : '#d1d5db',
                    }}>
                      {level}
                    </div>
                    <span className="text-[11px] font-semibold truncate" style={{ color: count > 0 ? COMP_COLORS[level] : '#9ca3af' }}>
                      {t(`homepage:homeBeta.severityLadder.${['base','lowCost','midCost','highCost','topCost'][level]}`, COMP_LABELS[level])}
                    </span>
                    <span className="text-[10px] ml-auto flex-shrink-0" style={{ color: T.textMuted }}>{count}</span>
                  </div>
                  {level === 0 ? (
                    <div className="text-[10px]" style={{ color: T.textMuted }}>
                      <span className="font-mono font-semibold" style={{ color: COMP_COLORS[0] }}>{fmt(baseCost)}</span>
                      <span className="ml-1">{t('homepage:homeBeta.severityLadder.base').toLowerCase()}</span>
                    </div>
                  ) : count > 0 ? (
                    <div className="text-[10px] font-mono font-semibold" style={{ color: COMP_COLORS[level] }}>
                      {r.min === r.max ? fmt(r.min) : `${fmt(r.min)}–${fmt(r.max)}`}
                    </div>
                  ) : (
                    <div className="text-[10px]" style={{ color: '#9ca3af' }}>—</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Disambiguation microcopy — explicit that "severity" here is
              cost-quartile, not clinical urgency. The labels (Base/Low-cost/
              Mid-cost/High-cost/Top-cost) match the dollar ranges shown above
              each pill. */}
          <p className="mt-3 text-[11px] italic" style={{ color: T.textMuted, lineHeight: 1.4 }}>
            <Trans
              i18nKey="homepage:homeBeta.severityLadder.microcopyRich"
              components={{ b: <span className="font-semibold not-italic" style={{ color: T.text }} /> }}
            />
          </p>
        </div>

        {/* Severity sections */}
        <div className="px-6 pb-6 space-y-4">
          {[0, 1, 2, 3, 4].map(level => {
            const scenarios = byLevel[level] || [];
            if (scenarios.length === 0) return (
              <div key={level} ref={(el) => { levelRefs.current[level] = el; }} className="flex items-center gap-3 p-3 rounded-lg" style={{
                backgroundColor: 'rgba(128,128,128,0.03)',
                border: '1px dashed rgba(128,128,128,0.15)',
              }}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: '#d1d5db' }}>
                  {level}
                </div>
                <div>
                  <div className="text-sm font-medium" style={{ color: '#9ca3af' }}>{t(`homepage:homeBeta.severityLadder.${['base','lowCost','midCost','highCost','topCost'][level]}`, COMP_LABELS[level])}</div>
                  <div className="text-xs" style={{ color: '#d1d5db' }}>{t('homepage:homeBeta.familyModal.noScenariosAtLevel', { defaultValue: 'No scenarios at this level' })}</div>
                </div>
              </div>
            );

            const displayed = scenarios;

            return (
              <div key={level} ref={(el) => { levelRefs.current[level] = el; }}>
                {/* Level header */}
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: COMP_COLORS[level] }}>
                    {level}
                  </div>
                  <span className="text-sm font-semibold" style={{ color: COMP_COLORS[level] }}>
                    {t(`homepage:homeBeta.severityLadder.${['base','lowCost','midCost','highCost','topCost'][level]}`, COMP_LABELS[level])}
                  </span>
                  {/* Base row gets an explicit "(with no complications)"
                      qualifier — disambiguates it from the variants above. */}
                  {level === 0 && (
                    <span className="text-xs italic" style={{ color: T.textMuted }}>
                      {t('homepage:homeBeta.familyModal.withNoComplications')}
                    </span>
                  )}
                  <span className="text-xs" style={{ color: T.textMuted }}>
                    {scenarios.length} {scenarios.length === 1 ? t('homepage:homeBeta.familyModal.scenarioSingular', { defaultValue: 'scenario' }) : t('homepage:homeBeta.familyModal.scenarioPlural', { defaultValue: 'scenarios' })}
                  </span>
                  <div className="h-px flex-1" style={{ backgroundColor: `${COMP_COLORS[level]}20` }} />
                </div>

                {/* Variant cards */}
                <div className="space-y-1.5">
                  {displayed.map(s => {
                    const id = s.id || s.uuid || '';
                    const cost = s.estimatedCost ?? s.estimated_cost ?? 0;
                    const realCost = s.realTotalCost ?? s.real_total_cost;
                    const displayCost = realCost || cost;
                    const delta = displayCost - baseCost;
                    const procs = s.totalProcedures ?? s.total_procedures ?? 0;
                    const pb = s.phaseBreakdown || {};
                    const phases = (s.phases || []).map((p: any) => typeof p === 'string' ? p : (p?.name || String(p)))
                      .sort((a: string, b: string) => (pb[b] || 0) - (pb[a] || 0));
                    const maxPh = Math.max(...Object.values(pb), 1);
                    const variantLabel = s.familyVariantLabel ?? s.family_variant_label;

                    return (
                      <div
                        key={id}
                        onClick={() => handleVariantClick(s)}
                        className="flex items-start gap-4 p-3 rounded-lg transition-all cursor-pointer"
                        style={{
                          backgroundColor: T.cardBg,
                          border: `1px solid ${T.border}`,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = COMP_COLORS[level];
                          e.currentTarget.style.boxShadow = `0 2px 12px ${COMP_COLORS[level]}15`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = T.border;
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate" style={{ color: T.text }}>
                              {s.name}
                            </span>
                          </div>
                          {variantLabel && variantLabel !== 'uncomplicated' && (
                            <div className="text-xs italic mt-0.5" style={{ color: T.textMuted }}>
                              {variantLabel}
                            </div>
                          )}
                          {/* Mini phase bars (top 3) */}
                          {phases.length > 0 && Object.keys(pb).length > 0 && (
                            <div className="space-y-0.5 mt-2">
                              {phases.slice(0, 3).map((phase: string, pIdx: number) => {
                                const pCost = pb[phase] || 0;
                                const pPct = Math.max((pCost / maxPh) * 100, 2);
                                return (
                                  <div key={phase} className="flex items-center gap-1.5">
                                    <span className="text-right flex-shrink-0 truncate" style={{ width: 80, color: T.textMuted, fontSize: 10 }}>
                                      {phase}
                                    </span>
                                    <div className="flex-1 h-2.5 rounded-sm" style={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                                      <div
                                        className="h-full rounded-sm"
                                        style={{
                                          width: `${pPct}%`,
                                          backgroundColor: phaseColors[pIdx % phaseColors.length],
                                          minWidth: 2,
                                        }}
                                      />
                                    </div>
                                    <span style={{ fontSize: 9, fontFamily: "'JetBrains Mono', monospace", color: T.textMuted, width: 40, textAlign: 'right', flexShrink: 0 }}>
                                      ${pCost >= 1000 ? `${(pCost / 1000).toFixed(0)}k` : Math.round(pCost).toLocaleString()}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Cost + delta + procedure count */}
                        <div className="text-right flex-shrink-0">
                          <div style={{ fontFamily: serif, fontSize: 20, color: T.red }}>
                            {fmtFull(displayCost)}
                          </div>
                          {level > 0 && delta !== 0 && (
                            <div className="text-[10px] font-mono mt-0.5" style={{ color: delta > 0 ? T.red : T.green }}>
                              {delta > 0 ? '+' : ''}{fmtFull(delta)}
                            </div>
                          )}
                          <div className="text-[10px] mt-0.5" style={{ color: T.textMuted }}>
                            {t('homepage:homeBeta.familyModal.proceduresCount', { count: procs, defaultValue: '{{count}} procedures' })}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
